import { createSelector } from 'reselect';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';

import models from '../../utilities/models.js';
import FlatCollection from '@chh/primitives/collection-flat.js';

import {
  POLICY_STATUSES,
  POLICIES_EU27_COUNTRYGROUP_COUNTRY_NUMERIC_IDS,
  POLICIES_STATUSES,
} from '../../constants.js';

function activeYearPredicate(datum, activeYear) {
  return activeYear.model.range
    ? datum.startYear >= activeYear.model.range[0] && datum.startYear <= activeYear.model.range[1]
    : datum.startYear <= activeYear.id && (!datum.endYear || datum.endYear >= activeYear.id);
}

function getActiveTypeIds(activeType) {
  return activeType.children
    ? activeType.children.map(function (child) {
        return child.model.id;
      })
    : [activeType.model.id];
}

export const selectActiveYear = (state) => state.activeYear;
export const selectActiveType = (state) => state.activeType;
export const selectHoveredType = (state) => state.hoveredType;
export const selectMapResults = (state) => state.results.map;
export const selectCountryResults = (state) => state.results.country;
export const selectTypes = (state) => state.types.data;
export const selectCurrentYear = (state) => new Date().getFullYear();
export const selectActiveRegion = (state) => state.activeRegion;
export const selectActivePolicyId = (state) => state.activePolicyId;

export const selectAvailableCountriesCollection = createSelector(
  selectMapResults,
  function (results) {
    const countries = results.data.map((datum) => datum.country.model);
    const sortedCountries = sortBy(countries, 'name');
    const collection = new FlatCollection(sortedCountries, true);
    collection.setDefault({ id: null, name: 'All countries' });
    return collection;
  }
);

export const selectMapData = createSelector(
  selectMapResults,
  selectActiveYear,
  selectActiveType,
  selectCurrentYear,
  selectTypes,
  function (results, activeYear, activeType, currentYear, types) {
    const activeTypeIds = getActiveTypeIds(activeType);

    return results.data.reduce(function (acc, datum) {
      const data = datum.data.filter(function (d) {
        return (
          (!activeType.id || activeTypeIds.includes(d.typeId)) &&
          (!activeYear.id || activeYearPredicate(d, activeYear))
        );
      });

      if (data.length) {
        acc.push({
          ...datum,
          data: data,
        });
      }
      return acc;
    }, []);
  }
);

// TODO: Merge with above selector?
export const selectFormattedMapData = createSelector(
  selectMapData,
  selectTypes,
  selectHoveredType,
  function (mapData, types, hoveredType) {
    return mapData.map(function (datum) {
      // const displayedType = datum.data.length
      //   ? datum.data.reduce((displayedType, datum) => {
      //       const type = types.get(datum.type);
      //       if (!displayedType || type.index > displayedType.index) {
      //         return type;
      //       }
      //       return displayedType;
      //     }, null)
      //   : undefined;
      // const hasHoveredType =
      //   hoveredType && datum.data.length
      //     ? datum.data.some((d) => d.type === hoveredType.id)
      //     : false;
      // const displayedColour = hasHoveredType
      //   ? hoveredType.model.icon?.colour
      //   : displayedType
      //   ? displayedType.model.icon?.colour
      //   : undefined;

      const maxStatus = datum.data[0].status
        ? datum.data.reduce((acc, x) => {
            const status = POLICIES_STATUSES.find((status) => status.id === x.status);
            if (!acc || status.index > acc.index) {
              return status;
            }
            return acc;
          }, undefined)
        : undefined;

      return {
        ...datum,
        // highlighted: datum.data.some((d) => !!d.status),
        // hovered: hoveredType ? hasHoveredType : undefined,
        colour: maxStatus ? maxStatus.colour : undefined,
        count: datum.data.length,
      };
    });
  }
);

export const selectListData = createSelector(selectFormattedMapData, function (mapData) {
  const keyedMapData = keyBy(mapData, 'countryId');
  const listData = [];

  models.countryGroupsRegions.forEach((region) => {
    const regionItem = {
      model: region.model,
      children: [],
    };

    region.children.forEach((country) => {
      if (keyedMapData[country.id]) {
        regionItem.children.push({
          model: country.model,
          data: keyedMapData[country.id],
        });
      }
    });

    if (regionItem.children.length) {
      regionItem.children = sortBy(regionItem.children, 'model.name');
      listData.push(regionItem);
    }
  });

  return listData;
});

export const selectPopulatedRegionIds = createSelector(selectListData, function (listData) {
  return listData.map((region) => {
    return region.model.id;
  });
});

export const selectHoveredCountryId = (state) => state.hoveredCountryId;

export const selectTooltipData = createSelector(
  selectHoveredCountryId,
  selectFormattedMapData,
  selectTypes,
  function (id, mapData, types) {
    if (!id) {
      return null;
    }

    const countryData = mapData.find((datum) => parseInt(datum.countryId, 10) === id);
    const hasStatuses = !!countryData.data[0].status;
    const hasCategories = !!countryData.data[0].category;

    const grouped = hasCategories
      ? groupBy(countryData.data, 'category')
      : hasStatuses
        ? groupBy(countryData.data, 'status')
        : groupBy(countryData.data, 'typeId');

    let total = 0;
    const summary = Object.keys(grouped).map((key, i) => {
      const count = grouped[key].length;
      // const statuses = grouped[typeId].reduce((acc, datum) => {
      //   if (datum.status && !acc.includes(datum.status)) {
      //     acc.push(datum.status);
      //   }
      //   return acc;
      // }, []);

      total += count;

      const type = types.get(key);
      const status = hasStatuses ? POLICIES_STATUSES.find((x) => x.id === key) : null;
      const category = hasCategories ? key : null;

      return {
        // type: hasStatuses ? { model: status } : types.get(typeId),
        index: status ? status.index : type && type.model.order ? type.model.order : i,
        title: category ? category : status ? status.name : type.model.name,
        colour: status ? status.colour : undefined,
        count: count,
        // FIXME: Only picking first policy status, probably want to prioritize one status type
        // status: statuses.length ? statuses[0] : null,
      };
    });

    const sortedSummary = hasCategories ? sortBy(summary, 'title') : sortBy(summary, 'index');

    return { data: sortedSummary, total: total };
  }
);

export const selectCountries = createSelector(selectFormattedMapData, function (mapData) {
  // Sort countries by highlighted status, so highlighted country labels are positioned first
  return mapData
    .sort((a, b) => {
      if (a.highlighted && !b.highlighted) {
        return -1;
      }
      if (!a.highlighted && b.highlighted) {
        return 1;
      }
      return 0;
    })
    .map((datum) => datum.country)
    .filter((country) => !!country);
});

export const selectEU27Countries = createSelector(function () {
  return POLICIES_EU27_COUNTRYGROUP_COUNTRY_NUMERIC_IDS.map((id) => {
    return models.countries.get(id);
  });
});

export const selectHoveredCountries = createSelector(selectFormattedMapData, function (mapData) {
  return mapData
    .filter((datum) => datum.hovered)
    .map((datum) => datum.country)
    .filter((country) => !!country);
});

export const selectCountryData = createSelector(
  selectCountryResults,
  selectActiveYear,
  selectActiveType,
  selectTypes,
  selectCurrentYear,
  function (results, activeYear, activeType, types, currentYear) {
    if (!results.data) {
      return null;
    }

    if (results.status !== 'ready') {
      return null;
    }

    let activeTypeIds = [activeType.id];

    console.log('countryData >>>', types, activeType, activeTypeIds);

    const data = results.data.data.filter(function (d) {
      return (
        (!activeType.id || activeTypeIds.includes(d.typeId)) &&
        (!activeYear.id || activeYearPredicate(d, activeYear))
      );
    });

    console.log('>>', data);

    /*
    const dataWithBranchType = data.map((x) => {
      return {
        ...x,
        branchType: types.getBranch(x.typeId).id,
      };
    });

    const dataGroupedByBranchType = groupBy(dataWithBranchType, 'branchType');

    const finalData = Object.keys(dataGroupedByBranchType).map(function (branchType) {
      const dataGroupedByType = groupBy(dataGroupedByBranchType[branchType], 'type');

      return {
        type: types.get(branchType),
        children: Object.keys(dataGroupedByType).map((type) => {
          return {
            policyType: types.get(type),
            children: dataGroupedByType[type],
          };
        }),
      };
    });
    */

    const dataGroupedByType = groupBy(data, 'typeId');

    const finalData = [];

    types.forEach((type) => {
      const childData = [];

      if (!type.children) {
        return;
      }

      type.children.forEach((childType) => {
        const typeId = childType.id;
        if (!activeTypeIds.includes(typeId) || !dataGroupedByType[typeId]) {
          return;
        }

        const hasCategories = dataGroupedByType[typeId][0].category;
        const hasStatuses = dataGroupedByType[typeId][0].status;

        let groupedData;
        if (hasCategories) {
          groupedData = groupBy(dataGroupedByType[typeId], 'category');
          childData.push({
            type: childType,
            count: dataGroupedByType[typeId].length,
            children: Object.keys(groupedData)
              .sort()
              .map((group) => {
                // Collapse category items into a list in a single item
                return {
                  title: group,
                  count: groupedData[group].length,
                  typeId: childType.id,
                  description: `
                    <ul>
                      ${groupedData[group].map((x) => `<li>${x.title}</li>`).join('')}
                    </ul>
                  `,
                };
              }),
          });
          return;
        } else if (hasStatuses) {
          groupedData = groupBy(dataGroupedByType[typeId], 'status');
        }

        childData.push({
          type: childType,
          count: dataGroupedByType[typeId].length,
          children: groupedData
            ? Object.keys(groupedData)
                .sort()
                .map((group) => {
                  const status = hasStatuses
                    ? POLICIES_STATUSES.find((x) => x.id === group)
                    : undefined;

                  return {
                    group: status ? status : group,
                    children: groupedData[group],
                  };
                })
            : dataGroupedByType[typeId],
        });
      });

      if (childData.length) {
        finalData.push({
          type: type,
          children: childData,
        });
      }
    });

    /*
    activeTypeIds.forEach(function (typeId) {
      if (!dataGroupedByType[typeId]) {
        return;
      }

      const hasCategories = dataGroupedByType[typeId][0].category;
      const hasStatuses = dataGroupedByType[typeId][0].status;

      let groupedData;
      if (hasCategories) {
        groupedData = groupBy(dataGroupedByType[typeId], 'category');
      } else if (hasStatuses) {
        groupedData = groupBy(dataGroupedByType[typeId], 'status');
      }

      finalData.push({
        type: types.get(typeId),
        children: groupedData
          ? Object.keys(groupedData)
              .sort()
              .map((group) => {
                const status = hasStatuses
                  ? POLICIES_STATUSES.find((x) => x.id === group)
                  : undefined;

                return {
                  group: status ? status : group,
                  children: groupedData[group],
                };
              })
          : dataGroupedByType[typeId],
      });
    });
    */

    console.log('final country data >>>', finalData);

    return finalData;
  }
);

export const selectActivePolicyData = createSelector(
  selectCountryResults,
  selectActivePolicyId,
  function (results, activePolicyId) {
    if (!results.data) {
      return null;
    }

    const policyData = results.data.data.find((x) => x.id === activePolicyId);

    return policyData;
  }
);

export const selectActivePolicyPolicyType = createSelector(
  selectActivePolicyData,
  selectTypes,
  function (policyData, types) {
    return policyData ? types.get(policyData.typeId) : undefined;
  }
);

// FIXME: It would be more consistent to use the model system as in the map view?
export const selectActiveRegionCountries = createSelector(selectActiveRegion, function (region) {
  if (!region || !region.model || !region.model.children) {
    return [];
  }

  return region.model.children.reduce((acc, id) => {
    const model = models.countries.get(id);
    if (model) {
      acc.push(model);
    }
    return acc;
  }, []);
});

export const selectActiveRegionCountriesWithData = createSelector(
  selectMapResults,
  selectActiveRegionCountries,
  function (results, countries) {
    if (!countries.length) {
      return [];
    }

    return countries.filter((country) => {
      return !!results.data.find((d) => d.country && d.country.id === country.id);
    });
  }
);

export const selectActiveTypeHasDescription = createSelector(selectActiveType, function (type) {
  return !!(type.model.description || (type.children && type.children[0].model.description));
});
